import React, { useMemo, useState } from "react";
import KuikaButton from "../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../ui-kit/kuika-input/kuika-input";
import KuikaSelectbox from "../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import "./board-workflows-page.scss";
import { BoardService } from "./board-service";
import ConfirmDialog from "./confirm-dialog";
import ProjectTransitions from "./project-transitions";
import { BoardStatus, StatusCategory } from "./types";
import { useBoardConfig } from "./use-board";

interface ProjectWorkflowProps {
  // The project whose statuses this manages. A board sees the union of its projects' statuses.
  projectKey: string;
}

interface CategoryOption {
  value: StatusCategory;
  label: string;
  color: string;
}

const CATEGORIES: CategoryOption[] = [
  { value: "todo", label: "To do", color: "var(--dark-4)" },
  { value: "in_progress", label: "In progress", color: "var(--blue-5)" },
  { value: "done", label: "Done", color: "var(--green-6)" }
];

function categoryColor(category: StatusCategory): string {
  return CATEGORIES.find((entry: CategoryOption) => entry.value === category)?.color ?? "var(--dark-4)";
}

// A project's workflow: the ordered statuses (todo / in_progress / done categories) issues flow
// through. Statuses are project-owned, so every board over the project shares them — this is the one
// place they're edited. Transition rules will land here as a sibling section.
const ProjectWorkflow: React.FC<ProjectWorkflowProps> = ({ projectKey }) => {
  const { config } = useBoardConfig();
  const canManage: boolean = config.canManage;
  const [newStatusName, setNewStatusName] = useState<string>("");
  const [newStatusCategory, setNewStatusCategory] = useState<StatusCategory>("todo");
  const [pendingStatusDelete, setPendingStatusDelete] = useState<BoardStatus | null>(null);

  const projectStatuses: BoardStatus[] = useMemo(
    () => config.statuses.filter((status: BoardStatus) => status.projectKey === projectKey),
    [config.statuses, projectKey]
  );

  const categoryOptions = CATEGORIES.map((entry: CategoryOption) => ({ value: entry.value, label: entry.label }));

  const moveStatus = (index: number, direction: -1 | 1): void => {
    const ids: string[] = projectStatuses.map((status: BoardStatus) => status.id);
    const target: number = index + direction;
    if (target < 0 || target >= ids.length) return;
    const next: string[] = [...ids];
    [next[index], next[target]] = [next[target]!, next[index]!];
    void BoardService.reorderStatuses(projectKey, next);
  };

  const addStatus = (): void => {
    const trimmed: string = newStatusName.trim();
    if (!trimmed) return;
    void BoardService.addStatus(projectKey, trimmed, newStatusCategory);
    setNewStatusName("");
    setNewStatusCategory("todo");
  };

  return (
    <div className="kuika-workflows">
      {!canManage && (
        <div className="kuika-workflows__readonly-note">
          <KuikaSvg name="lock" width={13} height={13} />
          View only — workspace owners or supervisors can change the workflow.
        </div>
      )}
      <section className="kuika-workflows__section">
        <div className="kuika-workflows__section-head">
          <div>
            <div className="kuika-workflows__section-title">Statuses</div>
            <div className="kuika-workflows__section-sub">
              Statuses are the states an issue moves through. They set the dot color and what counts as Done. Every
              board over this project shares them; each board groups them into columns its own way.
            </div>
          </div>
        </div>

        <div className="kuika-workflows__list">
          {projectStatuses.map((status: BoardStatus, index: number) => (
            <div key={status.id} className="kuika-workflows__row">
              {canManage && (
                <span className="kuika-workflows__reorder">
                  <button
                    type="button"
                    className="kuika-workflows__move"
                    disabled={index === 0}
                    onClick={() => moveStatus(index, -1)}
                    aria-label={`Move ${status.name} up`}
                    title="Move up"
                  >
                    <KuikaSvg name="chevron-up" type="arrows" width={11} height={11} />
                  </button>
                  <button
                    type="button"
                    className="kuika-workflows__move"
                    disabled={index === projectStatuses.length - 1}
                    onClick={() => moveStatus(index, 1)}
                    aria-label={`Move ${status.name} down`}
                    title="Move down"
                  >
                    <KuikaSvg name="chevron-down" type="arrows" width={11} height={11} />
                  </button>
                </span>
              )}
              <span className="kuika-workflows__dot" style={{ background: categoryColor(status.category) }} />
              {canManage ? (
                <KuikaInput
                  size="sm"
                  style={{ flex: 1, minWidth: 0 }}
                  value={status.name}
                  onChange={(_event, value: string | number) =>
                    BoardService.updateStatus(status.id, { name: String(value) })
                  }
                />
              ) : (
                <span className="kuika-workflows__readonly-name">{status.name}</span>
              )}
              {canManage ? (
                <span className="kuika-workflows__category">
                  <KuikaSelectbox
                    size="sm"
                    dropdownMatchSelectWidth={false}
                    value={status.category}
                    onChange={(value: string | number | (string | number)[] | null) =>
                      BoardService.updateStatus(status.id, { category: (value as StatusCategory) ?? "todo" })
                    }
                    options={categoryOptions}
                  />
                </span>
              ) : (
                <span className="kuika-workflows__category-label">
                  {CATEGORIES.find((entry: CategoryOption) => entry.value === status.category)?.label ??
                    status.category}
                </span>
              )}
              {canManage && (
                <button
                  type="button"
                  className="kuika-workflows__remove"
                  disabled={projectStatuses.length <= 1}
                  onClick={() => setPendingStatusDelete(status)}
                  aria-label={`Delete ${status.name}`}
                  title="Delete status"
                >
                  <KuikaSvg name="trash" width={14} height={14} />
                </button>
              )}
            </div>
          ))}
        </div>

        {canManage && (
          <div className="kuika-workflows__add">
            <KuikaInput
              size="sm"
              style={{ flex: 1, minWidth: 0 }}
              value={newStatusName}
              placeholder="New status name…"
              onChange={(_event, value: string | number) => setNewStatusName(String(value))}
            />
            <span className="kuika-workflows__category">
              <KuikaSelectbox
                size="sm"
                dropdownMatchSelectWidth={false}
                value={newStatusCategory}
                onChange={(value: string | number | (string | number)[] | null) =>
                  setNewStatusCategory((value as StatusCategory) ?? "todo")
                }
                options={categoryOptions}
              />
            </span>
            <KuikaButton type="primary" size="sm" disabled={!newStatusName.trim()} onClick={addStatus}>
              Add status
            </KuikaButton>
          </div>
        )}
      </section>

      <ProjectTransitions projectKey={projectKey} canManage={canManage} />

      <ConfirmDialog
        open={pendingStatusDelete !== null}
        title="Delete status?"
        message={`"${pendingStatusDelete?.name ?? ""}" will be removed and its issues move to the project's first status.`}
        confirmLabel="Delete"
        cancelLabel="Keep"
        danger
        onConfirm={() => {
          if (pendingStatusDelete) void BoardService.deleteStatus(pendingStatusDelete.id);
          setPendingStatusDelete(null);
        }}
        onCancel={() => setPendingStatusDelete(null)}
      />
    </div>
  );
};

export default ProjectWorkflow;
